/**
 * Date-range filter helpers for CompletedNotesPage.
 * Extracted so they can be unit-tested independently.
 */

export type DateRange = 'all' | '24h' | 'today' | 'yesterday' | '7d' | '30d' | '90d';

export interface DateFilter {
  start: Date;
  end?: Date;
}

/**
 * Returns a DateFilter describing the start (inclusive) and optional end
 * (inclusive) for the given range, or null for "all time".
 *
 * All boundaries are in local time, not UTC.
 */
export function getDateFilter(range: DateRange): DateFilter | null {
  const now = new Date();

  switch (range) {
    case 'today': {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return { start };
    }
    case 'yesterday': {
      const start = new Date(now);
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case '24h':
      return { start: new Date(now.getTime() - 24 * 60 * 60 * 1000) };
    case '7d':
      return { start: new Date(now.getTime() - 7 * 86_400_000) };
    case '30d':
      return { start: new Date(now.getTime() - 30 * 86_400_000) };
    case '90d':
      return { start: new Date(now.getTime() - 90 * 86_400_000) };
    default:
      return null;
  }
}

/**
 * Returns true if `date` falls within the filter window.
 */
export function matchesDateFilter(date: Date, filter: DateFilter | null): boolean {
  if (filter === null) return true;
  if (date < filter.start) return false;
  if (filter.end !== undefined && date > filter.end) return false;
  return true;
}
